import { FC, forwardRef, useEffect, useId, useState } from 'react';
import {
  IconButton,
  InputAdornment,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  Tooltip
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Custom styles for MDInput
import MDInputRoot from '@components/MDInput/MDInputRoot';
import { Colors } from '@constants/theme';
import FaIcon from '@components/FaIcon';
import MDBox from '@components/MDBox';

// Declaring props types for MDInput
interface Props
  extends Omit<OutlinedTextFieldProps | StandardTextFieldProps, 'variant'> {
  variant?: 'standard' | 'outlined';
  bgColor?: Colors;
  error?: boolean;
  success?: boolean;
  disabled?: boolean;
  needSearchIcon?: boolean;
  needEnterIcon?: boolean;
  onClear?: () => void;
  onClickEnter?: () => void;
  isLoading?: boolean;
  isSqBChatWidget?: boolean;
}

const MDInput: FC<Props | any> = forwardRef(
  (
    {
      error,
      success,
      disabled,
      needSearchIcon,
      needEnterIcon,
      bgColor,
      onClear,
      onClickEnter,
      isLoading,
      isSqBChatWidget,
      ...rest
    },
    ref
  ) => {
    const inputId = useId();

    const { value, placeholder } = rest;

    const [typedPlaceholder, setTypedPlaceholder] = useState('');
    const [placeholderIndex, setPlaceholderIndex] = useState(0);

    useEffect(() => {
      let timeoutId: NodeJS.Timeout;

      if (placeholder)
        timeoutId = setTimeout(() => {
          if (placeholderIndex % placeholder.length === 0)
            setTypedPlaceholder(placeholder[0]);
          else
            setTypedPlaceholder(
              (prev) =>
                prev + placeholder[placeholderIndex % placeholder.length]
            );
          setPlaceholderIndex((prev) => prev + 1);
        }, 80); // Adjust the typing speed as needed

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, [placeholder, placeholderIndex]);

    return (
      <MDInputRoot
        {...rest}
        ref={ref}
        ownerState={{
          error,
          success,
          disabled,
          bgColor
        }}
        id={inputId}
        InputProps={{
          placeholder: isSqBChatWidget ? typedPlaceholder : placeholder,
          startAdornment: needSearchIcon && (
            <label htmlFor={inputId}>
              <InputAdornment
                position='start'
                sx={{ marginLeft: '-4px', marginRight: 0 }}
              >
                <FontAwesomeIcon icon='magnifying-glass' />
              </InputAdornment>
            </label>
          ),
          endAdornment: needEnterIcon ? (
            <label id='end'>
              <Tooltip
                title={!isLoading ? 'Enter to submit' : ''}
                placement='right'
              >
                <span>
                  <InputAdornment
                    position='end'
                    sx={{ marginTop: 1, p: isSqBChatWidget ? 1 : 0 }}
                  >
                    {isLoading ? (
                      <MDBox
                        sx={{
                          marginBottom: 1
                        }}
                      >
                        <FaIcon icon='spinner-third' spin />
                      </MDBox>
                    ) : (
                      <FaIcon
                        icon='arrow-turn-down-left'
                        color={value ? 'dark' : 'grey'}
                        onClick={onClickEnter}
                        cursor='pointer'
                      />
                    )}
                  </InputAdornment>
                </span>
              </Tooltip>
            </label>
          ) : needSearchIcon ? (
            <IconButton
              onClick={onClear}
              sx={{
                opacity: value ? 1 : 0,
                pointerEvents: value ? 'all' : 'none',
                padding: 0
              }}
            >
              <label>
                <InputAdornment position='end'>
                  <FontAwesomeIcon icon='close' size='2xs' />
                </InputAdornment>
              </label>
            </IconButton>
          ) : (
            ''
          )
        }}
      />
    );
  }
);

// Declaring default props for MDInput
MDInput.defaultProps = {
  error: false,
  success: false,
  disabled: false
};

export default MDInput;
